import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import client, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { timeAgo } from "@/lib/format";
import { Building2, Search, Plus, ArrowRight, Mail, Phone, X, UploadCloud, FileSpreadsheet } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_STYLE = {
  "Clear": { fg: "#059669", bg: "#ECFDF5" },
  "Needs Attention": { fg: "#D97706", bg: "#FFFBEB" },
  "Update Due": { fg: "#D97706", bg: "#FFFBEB" },
};

const fieldCls = "h-9 w-full px-3 rounded-sm border border-line bg-surface text-sm text-ink focus:outline-none focus:ring-1 focus:ring-brand";

function CustomerStatus({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE["Clear"];
  return (
    <span data-testid={`customer-status-${status?.toLowerCase().replace(/\s+/g, "-")}`}
      className="inline-flex items-center rounded-sm font-semibold border px-2 py-0.5 text-[11px]"
      style={{ color: s.fg, backgroundColor: s.bg, borderColor: s.fg }}>
      {status}
    </span>
  );
}

function Labeled({ label, children }) {
  return <div><label className="op-label block mb-1.5">{label}</label>{children}</div>;
}

function AddCustomerDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", contact_person: "", email: "", phone: "", address: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    if (!form.name.trim()) return toast.error("Customer name is required.");
    setSaving(true);
    try {
      const r = await client.post("/customers", form);
      toast.success("Customer added");
      setOpen(false);
      setForm({ name: "", contact_person: "", email: "", phone: "", address: "" });
      onCreated(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not add customer.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-customer-btn"
          className="h-10 px-4 rounded-md bg-[#D97706] hover:bg-[#B45309] text-white text-[13.5px] font-black flex items-center gap-1.5 border-2 border-[#0B132B] shadow-[2px_2px_0px_0px_#0B132B] active:translate-y-0.5 active:translate-x-0.5 active:shadow-none transition-all">
          <span aria-hidden>➕</span> Add Customer
        </button>
      </DialogTrigger>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md rounded-md max-h-[90vh] !flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="font-heading text-[#0B132B] font-black">Add Customer</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-2">Create a customer profile to attach shipment jobs to.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="flex flex-col min-h-0 flex-1">
          <div className="space-y-3.5 px-6 py-3 overflow-y-auto flex-1 min-h-0">
            <Labeled label="Customer name"><input data-testid="customer-name-input" enterKeyHint="next" inputMode="text" className={fieldCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Blue Waters Resort" /></Labeled>
            <Labeled label="Primary contact person"><input data-testid="customer-contact-input" enterKeyHint="next" inputMode="text" className={fieldCls} value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} placeholder="Dahlia Lewis" /></Labeled>
            <div className="grid grid-cols-2 gap-3 [&>*]:min-w-0">
              <Labeled label="Email address"><input data-testid="customer-email-input" type="email" enterKeyHint="next" inputMode="email" className={fieldCls} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="ops@bluewaters.ag" /></Labeled>
              <Labeled label="Contact phone"><input data-testid="customer-phone-input" enterKeyHint="next" inputMode="tel" className={fieldCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+1 268 555 1234" /></Labeled>
            </div>
            <Labeled label="Billing / Delivery site address notes">
              <textarea data-testid="customer-address-input" enterKeyHint="done" rows={3} value={form.address} onChange={(e) => set("address", e.target.value)}
                className="w-full px-3 py-2 rounded-sm border border-line bg-surface text-sm text-ink focus:outline-none focus:ring-1 focus:ring-brand resize-none" placeholder="Northern delivery gate; guard holds manifest. Invoice to head office, St. John's." />
            </Labeled>
          </div>
          <div className="bg-white border-t-2 border-[#0B132B] p-4 shrink-0">
            <button type="submit" data-testid="customer-submit-btn" disabled={saving}
              className="w-full bg-[#D97706] hover:bg-[#B45309] text-white font-black py-3 rounded-md border-2 border-[#0B132B] shadow-[2px_2px_0px_0px_#0B132B] active:translate-y-0.5 active:translate-x-0.5 active:shadow-none transition-all disabled:opacity-60">
              {saving ? "Saving…" : "Create Customer"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function parseCsv(text) {
  const rows = text.replace(/\r\n/g, "\n").split("\n").filter((l) => l.trim().length);
  if (!rows.length) return { headers: [], rows: [], count: 0 };
  const splitLine = (l) => l.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
  const headers = splitLine(rows[0]);
  const body = rows.slice(1).map(splitLine);
  return { headers, rows: body, count: body.length };
}

function BulkUploadDialog() {
  const [open, setOpen] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [fileName, setFileName] = useState("");
  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        setParsed(parseCsv(String(reader.result || "")));
      } catch {
        toast.error("Could not read that CSV file.");
      }
    };
    reader.readAsText(file);
  };
  const reset = () => { setParsed(null); setFileName(""); };
  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <button data-testid="bulk-upload-btn"
          className="h-10 px-4 rounded-md bg-white text-[#0B132B] text-[13.5px] font-bold flex items-center gap-1.5 border-2 border-[#0B132B] shadow-[2px_2px_0px_0px_#0B132B] hover:bg-[#F8FAFC] active:translate-y-0.5 active:translate-x-0.5 active:shadow-none transition-all">
          <span aria-hidden>📥</span> Upload Client List
        </button>
      </DialogTrigger>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md rounded-md max-h-[90vh] !flex flex-col p-0 gap-0" data-testid="bulk-upload-modal">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="font-heading text-[#0B132B] font-black">Upload Client List</DialogTitle>
          <DialogDescription className="text-[13px] text-ink-2">Import your corporate roster spreadsheet (.csv) for schema allocation mapping.</DialogDescription>
        </DialogHeader>
        <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0 space-y-4">
          {!parsed ? (
            <>
              <label data-testid="bulk-upload-dropzone"
                className="flex flex-col items-center justify-center text-center gap-2 border-2 border-dashed border-[#D97706] rounded-md px-4 py-8 cursor-pointer hover:bg-[#FFFBEB] transition-colors">
                <UploadCloud className="h-7 w-7 text-[#D97706]" strokeWidth={1.75} />
                <span className="text-[13.5px] font-bold text-[#0B132B]">Select your corporate roster spreadsheet</span>
                <span className="text-[12px] text-ink-3">Accepted format: .csv &middot; columns are auto-detected for schema mapping</span>
                <input data-testid="bulk-upload-input" type="file" accept=".csv" className="hidden" onChange={onFile} />
              </label>
              <div className="border border-line rounded-sm bg-surface-alt/50 p-3 text-[12px] text-ink-2 leading-relaxed">
                <p className="op-label mb-1.5 flex items-center gap-1.5"><FileSpreadsheet className="h-3.5 w-3.5" /> Expected columns</p>
                Name, Primary Contact, Email, Phone, Address. Your file is read locally in your browser — no data is committed until automated schema mapping is enabled.
              </div>
            </>
          ) : (
            <div data-testid="bulk-upload-preview" className="space-y-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] font-bold text-[#0B132B] truncate">{fileName}</span>
                <button data-testid="bulk-upload-reset" onClick={reset} className="text-[12px] text-ink-3 hover:text-ink flex items-center gap-1 shrink-0"><X className="h-3.5 w-3.5" /> Clear</button>
              </div>
              <div className="border border-line rounded-sm p-3 bg-surface-alt/50">
                <p className="text-[13px] text-ink"><span className="font-mono-data font-black text-[#0B132B]">{parsed.count}</span> client rows detected across <span className="font-mono-data font-black text-[#0B132B]">{parsed.headers.length}</span> columns.</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {parsed.headers.map((h, i) => (
                    <span key={i} className="text-[11px] px-2 py-0.5 rounded-sm border border-[#0B132B]/40 bg-white text-[#0B132B] font-medium">{h || `col ${i + 1}`}</span>
                  ))}
                </div>
              </div>
              <div className="border-2 border-[#D97706] bg-[#FFFBEB] rounded-sm p-3 text-[12px] text-ink-2 leading-relaxed" data-testid="bulk-upload-notice">
                Detected columns are staged for <strong className="text-[#0B132B]">automated schema allocation mapping</strong>. Bulk commit to the workspace directory is coming soon — for now, add priority clients via <strong className="text-[#0B132B]">➕ Add Customer</strong>.
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const FILTERS = [
  { key: "all", label: "All Customers" },
  { key: "active", label: "Has Active Jobs" },
  { key: "issues", label: "Open Issues" },
  { key: "recent", label: "Recently Updated" },
];

export default function Customers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const load = () => client.get("/customers").then((r) => setCustomers(r.data));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = customers;
    const q = query.trim().toLowerCase();
    if (q) list = list.filter((c) =>
      [c.name, c.contact_person, c.email, c.phone].some((v) => (v || "").toLowerCase().includes(q)));
    if (filter === "active") list = list.filter((c) => c.active_jobs > 0);
    if (filter === "issues") list = list.filter((c) => c.open_issues > 0);
    if (filter === "recent") list = [...list].sort((a, b) => (b.last_update_sent || "").localeCompare(a.last_update_sent || ""));
    return list;
  }, [customers, query, filter]);

  const count = (key) => {
    if (key === "active") return customers.filter((c) => c.active_jobs > 0).length;
    if (key === "issues") return customers.filter((c) => c.open_issues > 0).length;
    return customers.length;
  };

  return (
    <Layout>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <p className="op-label mb-1">Relationships</p>
          <h1 className="font-heading text-2xl font-semibold text-ink">Customers</h1>
          <p className="text-sm text-ink-2 mt-1">Who has active work, who has issues, and who's due for an update.</p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-2.5" data-testid="customers-action-panel">
            <BulkUploadDialog />
            <AddCustomerDialog onCreated={load} />
          </div>
        )}
      </div>

      {/* Filters + search */}
      <div className="bg-surface border border-line rounded-md p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button key={f.key} data-testid={`customer-filter-${f.key}`} onClick={() => setFilter(f.key)}
              className={`h-8 px-3 rounded-sm text-[12.5px] font-medium border transition-colors flex items-center gap-1.5 ${
                filter === f.key ? "bg-brand text-white border-brand" : "bg-surface text-ink-2 border-line hover:border-steel"}`}>
              {f.label}
              {f.key !== "recent" && <span className={`font-mono-data text-[10.5px] ${filter === f.key ? "text-white/70" : "text-ink-3"}`}>{count(f.key)}</span>}
            </button>
          ))}
        </div>
        <div className="relative ml-auto w-full sm:w-72">
          <Search className="h-4 w-4 text-ink-3 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="customer-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, contact, email…"
            className="w-full h-9 pl-9 pr-3 rounded-sm border border-line bg-surface-alt text-sm text-ink placeholder:text-ink-3 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface border border-line rounded-md overflow-hidden">
        <div className="overflow-x-auto mv-scroll">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line bg-surface-alt">
                {["Customer", "Contact", "Active", "Issues", "Total", "Last Update", "Status", ""].map((h) => (
                  <th key={h} className="op-label px-4 py-2.5 whitespace-nowrap font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {filtered.map((c) => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`}
                  tabIndex={0}
                  onClick={() => navigate(`/app/customers/${c.id}`)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); navigate(`/app/customers/${c.id}`); } }}
                  className="hover:bg-surface-alt transition-colors cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-steel/40">
                  <td className="px-4 py-3">
                    <div className="text-[13.5px] text-ink font-medium whitespace-nowrap">{c.name}</div>
                    <div className="text-[11.5px] text-ink-3 flex items-center gap-2 mt-0.5">
                      {c.email && <span className="truncate max-w-[180px]">{c.email}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[13px] text-ink-2 whitespace-nowrap">
                    <div>{c.contact_person || "—"}</div>
                    {c.phone && <div className="text-[11.5px] text-ink-3 mt-0.5">{c.phone}</div>}
                  </td>
                  <td className="px-4 py-3 font-mono-data text-[12.5px] text-ink whitespace-nowrap">{c.active_jobs}</td>
                  <td className="px-4 py-3 font-mono-data text-[12.5px] whitespace-nowrap" style={{ color: c.open_issues > 0 ? "#DC2626" : "#64748B" }}>{c.open_issues}</td>
                  <td className="px-4 py-3 font-mono-data text-[12.5px] text-ink-2 whitespace-nowrap">{c.total_jobs}</td>
                  <td className="px-4 py-3 text-[12.5px] text-ink-2 whitespace-nowrap">{c.last_update_sent ? timeAgo(c.last_update_sent) : "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap"><CustomerStatus status={c.status_indicator} /></td>
                  <td className="px-4 py-3"><ArrowRight className="h-4 w-4 text-ink-3 opacity-0 group-hover:opacity-100 transition-opacity" /></td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-ink-3">No customers match this view.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
